// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * analysis.ts — pure analytics: auto-detected insights, movers and rankings.
 * Everything derives from the Dataset; no DOM here.
 */
import type { Dataset } from './data';
import { materialsForYear, statesForYear, filterRows, total, recovered, recoveryRate, materialSeries } from './data';
import { catInfo, jurisName } from './domain';
import { formatTonnes, formatPercent, fyShort } from './utils/format';

export type Severity = 'info' | 'warn' | 'alert' | 'good';
export interface InsightLink { kind: 'material' | 'state' | 'view'; key: string; label: string }
export interface Insight { severity: Severity; title: string; body: string; links: InsightLink[] }

export interface Mover { key: string; label: string; first: number; last: number; delta: number; lastTonnes: number }

/** Recovery-rate change per material, first year → latest. */
export function materialMovers(d: Dataset): Mover[] {
  return d.catKeys
    .map((key) => {
      const series = materialSeries(d, key);
      const first = series[0];
      const last = series[series.length - 1];
      return { key, label: catInfo(key).short, first: first.rate, last: last.rate, delta: last.rate - first.rate, lastTonnes: last.total };
    })
    .filter((m) => m.lastTonnes > 0)
    .sort((a, b) => b.delta - a.delta);
}

function median(nums: number[]): number {
  const s = [...nums].filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!s.length) return 0;
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function insights(d: Dataset): Insight[] {
  const y = d.latestYear;
  const out: Insight[] = [];
  const mats = materialsForYear(d, y);
  const withRate = mats.filter((m) => m.total > 0);

  // 1. Plastics / worst-recovered material
  const worst = [...withRate].sort((a, b) => a.rate - b.rate)[0];
  if (worst) {
    const landfill = worst.total - worst.rec;
    out.push({
      severity: 'alert',
      title: `${catInfo(worst.key).short} is recovered least — just ${formatPercent(worst.rate, 0)}`,
      body: `Of the ${formatTonnes(worst.total)} of ${catInfo(worst.key).short.toLowerCase()} discarded in ${fyShort(y)}, ${formatTonnes(landfill)} was disposed of rather than recovered. It is the lowest recovery rate of any material category.`,
      links: [{ kind: 'material', key: worst.key, label: `${catInfo(worst.key).short} detail` }],
    });
  }

  // 2. Best-recovered material (contrast)
  const best = [...withRate].sort((a, b) => b.rate - a.rate)[0];
  const metals = withRate.find((m) => m.key === 'metals');
  const plastics = withRate.find((m) => m.key === 'plastics');
  if (best && plastics && metals && best.key !== worst?.key) {
    out.push({
      severity: 'good',
      title: `Metals ${formatPercent(metals.rate, 0)} vs plastics ${formatPercent(plastics.rate, 0)} — a ${(metals.rate / Math.max(1, plastics.rate)).toFixed(1)}× gap`,
      body: `Recovery depends almost entirely on the material. Valuable metals are recovered at ${formatPercent(metals.rate, 0)} and building rubble at ${formatPercent(withRate.find((m) => m.key === 'building-demolition')?.rate ?? 0, 0)}, but lightweight, low-value plastics are mostly landfilled.`,
      links: [{ kind: 'view', key: 'materials', label: 'Compare materials' }],
    });
  }

  // 3. National recovery trend + target gap
  const rateNow = recoveryRate(filterRows(d.rows, { yr: y }));
  const rateThen = recoveryRate(filterRows(d.rows, { yr: d.firstYear }));
  out.push({
    severity: 'info',
    title: `Recovery has risen from ${formatPercent(rateThen, 0)} to ${formatPercent(rateNow, 0)} — but the 2030 target is ${d.target.recoveryRate}%`,
    body: `Australia now keeps ${formatPercent(rateNow, 0)} of its waste out of landfill, up from ${formatPercent(rateThen, 0)} in ${fyShort(d.firstYear)}. Reaching the ${d.target.recoveryRate}% ${d.target.label} by ${d.target.year} means closing a ${formatPercent(d.target.recoveryRate - rateNow, 0)} gap.`,
    links: [{ kind: 'view', key: 'trends', label: 'See the trend' }],
  });

  // 4. Biggest improver
  const movers = materialMovers(d);
  const up = movers[0];
  if (up && up.delta > 5) {
    out.push({
      severity: 'good',
      title: `${up.label} recovery jumped ${up.delta.toFixed(0)} points since ${fyShort(d.firstYear)}`,
      body: `${up.label} recovery went from ${formatPercent(up.first, 0)} to ${formatPercent(up.last, 0)} over the ${d.years.length} reporting years — the biggest improvement of any material.`,
      links: [{ kind: 'material', key: up.key, label: `${up.label} detail` }],
    });
  }

  // 5. Total waste growth
  const totNow = total(filterRows(d.rows, { yr: y }));
  const totThen = total(filterRows(d.rows, { yr: d.firstYear }));
  const growth = ((totNow - totThen) / totThen) * 100;
  out.push({
    severity: growth > 0 ? 'warn' : 'good',
    title: `Australia generates ${formatTonnes(totNow)} of waste a year — ${growth > 0 ? 'up' : 'down'} ${Math.abs(growth).toFixed(0)}% since ${fyShort(d.firstYear)}`,
    body: `Total waste rose from ${formatTonnes(totThen)} to ${formatTonnes(totNow)}. Recovery is improving faster than generation, but the total pile keeps growing with the economy and population.`,
    links: [{ kind: 'view', key: 'trends', label: 'Waste over time' }],
  });

  // 6. Per-capita outlier states
  const states = statesForYear(d, y).filter((s) => s.perCapita != null);
  const med = median(states.map((s) => s.perCapita as number));
  const high = [...states].sort((a, b) => (b.perCapita as number) - (a.perCapita as number))[0];
  if (high && med > 0 && (high.perCapita as number) > med * 1.25) {
    out.push({
      severity: 'info',
      title: `${jurisName(high.code)} throws out the most per person — ${(high.perCapita as number).toFixed(1)} t each`,
      body: `Per resident, ${jurisName(high.code)} generates ${(high.perCapita as number).toFixed(1)} tonnes a year against a national median of ${med.toFixed(1)} t — largely construction and industrial waste, not household bins.`,
      links: [{ kind: 'state', key: high.code, label: `${jurisName(high.code)} detail` }],
    });
  }

  // 7. Stream contrast (household vs construction recovery)
  const msw = d.streamRows.filter((r) => r.yr === y && r.s === 'MSW');
  const cd = d.streamRows.filter((r) => r.yr === y && r.s === 'C&D');
  const mswRate = msw.length ? (msw.reduce((a, r) => a + (isRec(r.f) ? r.t : 0), 0) / msw.reduce((a, r) => a + r.t, 0)) * 100 : 0;
  const cdRate = cd.length ? (cd.reduce((a, r) => a + (isRec(r.f) ? r.t : 0), 0) / cd.reduce((a, r) => a + r.t, 0)) * 100 : 0;
  out.push({
    severity: 'info',
    title: `Construction waste is recovered better (${formatPercent(cdRate, 0)}) than the household bin (${formatPercent(mswRate, 0)})`,
    body: `The kerbside stream you control at home recovers about ${formatPercent(mswRate, 0)} of its waste — less than the ${formatPercent(cdRate, 0)} recovered from construction and demolition, where heavy concrete and metal are easy to recycle.`,
    links: [{ kind: 'view', key: 'streams', label: 'Compare streams' }],
  });

  return out;
}

function isRec(f: string): boolean { return f === 'recycling' || f === 'energy' || f === 'reuse'; }

// re-exported for views
export { materialsForYear, statesForYear, recovered, total, recoveryRate };
